'use client';
import {
	useGetAreas,
	useGetProperties,
	useGetSectors,
} from '@practium/features/api';
import { useMemo } from 'react';

interface FormWithLocation {
	property: string;
	area: string;
	sector: string[];
}

const getRefId = (value: unknown) => {
	if (typeof value === 'string') return value;
	if (!value || typeof value !== 'object') return '';
	const reference = value as { _id?: unknown; id?: unknown };
	const id = reference._id ?? reference.id;
	if (typeof id === 'string') return id;
	if (id == null) return '';
	const asString = String(id);
	return asString && asString !== '[object Object]' ? asString : '';
};

export const useLocationOptions = (form: FormWithLocation) => {
	const { data: properties = [] } = useGetProperties({
		limit: 300,
		fetchAllPages: true,
	});
	const { data: areas = [] } = useGetAreas({
		limit: 300,
		enabled: !!form.property,
	});
	const { data: sectors = [] } = useGetSectors({
		areaId: form.area || undefined,
		enabled: !!form.area,
		fetchAllPages: true,
	});

	// TODO: Replace local filtering when the areas endpoint supports a reliable propertyId filter.
	const filteredAreas = useMemo(
		() => (form.property ? areas.filter((a: any) => getRefId(a.propId) === form.property) : []),
		[form.property, areas]
	);

	const propertyOptions = useMemo(
		() => properties.map((p: any) => ({ value: p._id, label: p.name })),
		[properties]
	);

	const areaOptions = useMemo(
		() => filteredAreas.map((a: any) => ({ value: a._id ?? a.name, label: a.name })),
		[filteredAreas]
	);

	const sectorOptions = useMemo(
		() => sectors.map((s: any) => ({ value: s._id, label: s.name })),
		[sectors]
	);

	const sectorAreas = useMemo(
		() =>
			sectors.reduce<Record<string, number>>((acc, sector: any) => {
				acc[sector._id] = Number(sector.size ?? 0);
				return acc;
			}, {}),
		[sectors]
	);

	const selectedSectorNames = useMemo(
		() =>
			form.sector
				.map((id) => sectors.find((s: any) => s._id === id)?.name)
				.filter(Boolean)
				.join(', '),
		[form.sector, sectors]
	);

	return {
		properties,
		areas,
		propertyOptions,
		areaOptions,
		sectorOptions,
		selectedSectorNames,
		sectorAreas,
	};
};
